import { DateTime } from "luxon";
import { useEffect, useRef } from "react";
import type { KlusModel } from "../../models/KlusModel";
import { toStringDate } from "../../utils/ToStringDate";
import { DATETIME_FORMAT_NO_DATE_NO_SECONDS } from "@shared/common/utils/luxonHelpers";

interface TractieDialogElement extends HTMLElement {
  toggleContainer?: (open: boolean) => void;
}

interface Props {
  klusToBeDeleted: KlusModel | null;
  onClose: () => void;
  onConfirm: (id: number) => void;
}

export function DeleteKlusDialog({ klusToBeDeleted, onClose, onConfirm }: Readonly<Props>) {
  const dialogRef = useRef<TractieDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    void customElements.whenDefined("tractie-dialog").then(() => {
      dialog.toggleContainer?.(!!klusToBeDeleted);
    });
  }, [klusToBeDeleted]);

  const handleClose = () => {
    const dialog = dialogRef.current;

    if (dialog?.toggleContainer) {
      dialog.toggleContainer(false);
    } else {
      dialog?.removeAttribute("open");
    }

    onClose();
  };

  const formatTime = (date: DateTime) =>
    DateTime.fromISO(toStringDate(date)).toFormat(DATETIME_FORMAT_NO_DATE_NO_SECONDS);

  const formatTimeRange = (k: KlusModel) =>
    `${formatTime(k.beginDatumtijd)} - ${formatTime(k.eindDatumtijd)}`;

  return (
    <tractie-dialog
      data-testid="delete-klus-dialog"
      ref={dialogRef}
      size="xs"
      ondialog-close={handleClose}
    >
      <h3 slot="start">Klus verwijderen</h3>

      <div>
        {klusToBeDeleted ? (
          <>
            Weet je zeker dat je {klusToBeDeleted.klusSoort} ({formatTimeRange(klusToBeDeleted)})
            wilt verwijderen?
          </>
        ) : null}
      </div>

      <tractie-button
        data-testid="delete-klus-cancel-button"
        slot="end"
        level="tertiary"
        onClick={handleClose}
      >
        Annuleren
      </tractie-button>

      <tractie-button
        data-testid="delete-klus-confirm-button"
        slot="end"
        level="primary"
        appearance="destructive"
        onClick={() => {
          if (klusToBeDeleted?.id != null) {
            onConfirm(klusToBeDeleted.id);
          }
          handleClose();
        }}
      >
        Verwijderen
      </tractie-button>
    </tractie-dialog>
  );
}
